/**
 * Inline-editable rows for the Promotion Summary card.
 *
 * These match the app's generic inline-field row system (see InlineFieldRow):
 * label left, value right, `text-sm` foreground on both, generous padding, and
 * click-the-row-to-edit. The editors reuse the shared inline primitives
 * (`InlineFieldInput` Currency + `InlineDatePicker`, `INLINE_INPUT_CLASS`) so the
 * inputs are visually identical to the rest of the app.
 *
 * Persistence goes through the shared `pricingRules.update` helper. Since the host
 * StandardDetailPage does NOT refetch its formData after an external write, each
 * row keeps an optimistic `committed*` value as its display source of truth
 * (re-synced only when formData genuinely changes — external edit / full reload).
 *
 *   • FieldRow     — read-only generic-style row (used for structural fields)
 *   • DateRangeRow — valid_from → valid_upto (two InlineDatePickers)
 *   • MinSpendRow  — min_amt (InlineFieldInput, Currency)
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, ChevronRight, Package } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InlineFieldInput } from "@/components/inline-edit/detail/InlineFieldInput";
import type { DocFieldSchema } from "@/lib/erp/meta";
import { useDetailDensity } from "@/features/inventory/item-detail/hooks/useDetailDensity";
import { useStoreList } from "@/hooks/useStoreList";
import { pricingRules } from "@/lib/erp/pricingRules";
import { formatMoney, formatReadableDate } from "@/features/customers/customer-detail/customerDetailPrimitives";
import { windowSummary, ruleCurrency, appliesToSummary, type Lang } from "./pricingRuleDetailModel";
import { usePricingRuleItems } from "./usePricingRuleCoupons";
import { StorefrontScopePicker } from "./StorefrontScopePicker";

const pick = (lang: Lang, th: string, en: string) => (lang === "th" ? th : en);

// Minimal field schema so the shared Currency editor formats/parses correctly.
const MIN_AMT_FIELD: DocFieldSchema = { fieldname: "min_amt", label: "Minimum spend", fieldtype: "Currency" };

/** Parse a YYYY-MM-DD (or ISO) string to a Date, or undefined. */
function toDate(s?: string): Date | undefined {
  if (!s) return undefined;
  const d = new Date(String(s).includes("T") ? s : `${s}T00:00:00`);
  return isNaN(d.getTime()) ? undefined : d;
}

/** Format a Date to Frappe's YYYY-MM-DD. */
function fmtYMD(d?: Date): string {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Shared hook: persist a partial patch to this Pricing Rule and refresh the doc. */
function useRulePatch(docName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) => pricingRules.update(docName, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get", "Pricing Rule", docName] });
    },
  });
}

/**
 * Generic-style row shell — mirrors InlineFieldRow: foreground label on the left,
 * a right-aligned value area, click-the-row-to-edit. Sizing is driven entirely by
 * the shared `useDetailDensity()` hook (rowPx/rowPy/rowGap/rowLabel) — the SAME
 * source InlineFieldRow reads — so this card tracks the user's density (Compact/
 * Spacious) and text-size prefs exactly like every other detail card.
 */
function RowShell({
  label,
  children,
  onEdit,
}: {
  label: string;
  children: React.ReactNode;
  onEdit?: () => void;
}) {
  const { rowPx, rowPy, rowGap, rowLabel } = useDetailDensity();
  return (
    <div
      className={cn(
        "flex items-center transition-colors",
        rowGap,
        rowPx,
        rowPy,
        onEdit && "cursor-text hover:bg-muted/30 dark:hover:bg-white/[0.03]",
      )}
      onClick={onEdit}
    >
      <span className={cn("shrink-0 text-gray-900 dark:text-gray-100", rowLabel)}>{label}</span>
      <div
        className={cn(
          "flex min-w-0 flex-1 items-center justify-end gap-2 text-right text-gray-900 dark:text-gray-100",
          rowLabel,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** Read-only generic-style row for structural fields (applies-to, store). */
export function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <RowShell label={label}>
      <span className="inline-flex items-center gap-1.5 break-words">{value}</span>
    </RowShell>
  );
}

/**
 * ใช้กับ (Applies to) — when a rule targets specific products (apply_on ===
 * "Item Code"), resolve each item_code to its real product and show item_name +
 * a thumbnail (item.image, a direct URL — rendered exactly like ProductListView /
 * BundleComponentsSection). Each product links to its detail page so the owner can
 * jump straight to what this promotion discounts instead of decoding a warehouse
 * code like `CS-BDL-KRM-W`. Falls back to the plain code/summary while the item
 * docs load or when nothing resolves.
 */
export function AppliesToProductsRow({
  label,
  formData,
  lang,
}: {
  label: string;
  formData: Record<string, any>;
  lang: Lang;
}) {
  const codes = ((formData.items ?? []) as { item_code?: string }[])
    .map((i) => i.item_code)
    .filter((c): c is string => !!c);
  // Some rules store a single target on the parent doc rather than the child table.
  const targetCodes = codes.length > 0 ? codes : formData.item_code ? [String(formData.item_code)] : [];

  const { data: items } = usePricingRuleItems(targetCodes, targetCodes.length > 0);

  // Nothing to resolve → keep the generic text summary (Item Group / Brand / cart).
  if (targetCodes.length === 0) {
    return <FieldRow label={label} value={appliesToSummary(formData, lang)} />;
  }

  const byCode = new Map((items ?? []).map((it) => [it.item_code, it]));

  return (
    <RowShell label={label}>
      <div className="flex min-w-0 flex-col items-end gap-1.5">
        {targetCodes.map((code) => {
          const it = byCode.get(code);
          return (
            <Link
              key={code}
              to={`/inventory/item/${encodeURIComponent(code)}`}
              className="group inline-flex max-w-full items-center gap-2 hover:opacity-90"
            >
              {it?.image ? (
                <img
                  src={it.image}
                  alt={it.item_name || code}
                  className="h-7 w-7 shrink-0 rounded-md object-cover ring-1 ring-border/60"
                />
              ) : (
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted ring-1 ring-border/60">
                  <Package className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
              )}
              <span className="min-w-0 truncate group-hover:text-sky-600 dark:group-hover:text-sky-400">
                {it?.item_name || code}
              </span>
              <ChevronRight className="!h-3.5 !w-3.5 shrink-0 text-gray-300 transition-colors group-hover:text-sky-500 dark:text-gray-600" />
            </Link>
          );
        })}
      </div>
    </RowShell>
  );
}

/** The thin inset divider used between generic field rows (density-tracked inset). */
export function RowDivider() {
  const { dividerMx } = useDetailDensity();
  return <div className={cn(dividerMx, "border-t border-border/40")} />;
}

/**
 * ระยะเวลา — edit valid_from → valid_upto with a single range calendar popover
 * (Calendar mode="range"), the proper UX for a date range. Both dates persist in
 * one patch only once a complete range is chosen, so a live rule never gets a
 * half-cleared window.
 */
export function DateRangeRow({
  label,
  formData,
  docName,
  lang,
}: {
  label: string;
  formData: Record<string, any>;
  docName: string;
  lang: Lang;
}) {
  const [open, setOpen] = useState(false);
  const [committedFrom, setCommittedFrom] = useState<string>(formData.valid_from || "");
  const [committedUpto, setCommittedUpto] = useState<string>(formData.valid_upto || "");
  // Draft range shown live in the calendar while the user is mid-selection.
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);
  const patch = useRulePatch(docName);

  useEffect(() => setCommittedFrom(formData.valid_from || ""), [formData.valid_from]);
  useEffect(() => setCommittedUpto(formData.valid_upto || ""), [formData.valid_upto]);

  const committedRange: DateRange = { from: toDate(committedFrom), to: toDate(committedUpto) };
  const shown = draft ?? committedRange;
  const canApply = !!shown.from && !!shown.to && !patch.isPending;

  // Quick actions for a LIVE coupon: write the exact same valid_upto field the
  // calendar persists (verified write path) with the same optimistic committed
  // value + toast handling — no two-month calendar, no hand-picking a date.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = toDate(committedUpto);
  const alreadyEnded = !!endDate && endDate.getTime() <= today.getTime();

  const applyUpto = async (to: string, successMsg: string) => {
    if (patch.isPending) return;
    const prevUpto = committedUpto;
    setCommittedUpto(to); // optimistic — host formData doesn't refetch after write
    try {
      await patch.mutateAsync({ valid_upto: to });
      toast.success(successMsg);
    } catch {
      setCommittedUpto(prevUpto);
      toast.error(pick(lang, "บันทึกไม่สำเร็จ ลองอีกครั้ง", "Couldn’t save, try again"));
    }
  };

  // Quick actions are consequential on a LIVE rule, so they go through a confirm
  // step (double-action) — End-now is destructive (stops the coupon immediately).
  const [confirm, setConfirm] = useState<null | "end" | "extend">(null);

  const extendBase = endDate && endDate.getTime() > today.getTime() ? new Date(endDate) : new Date(today);
  const extendTarget = new Date(extendBase);
  extendTarget.setDate(extendTarget.getDate() + 7);

  const endNow = () => applyUpto(fmtYMD(today), pick(lang, "สิ้นสุดโปรโมชันแล้ว", "Promotion ended"));
  const extend7 = () => applyUpto(fmtYMD(extendTarget), pick(lang, "ต่ออายุ 7 วันแล้ว", "Extended 7 days"));

  const runConfirmed = () => {
    if (confirm === "end") endNow();
    else if (confirm === "extend") extend7();
    setConfirm(null);
  };

  // The calendar only updates the draft — persistence happens on Apply, so an
  // accidental re-click (which RDP collapses to a single day) never touches a
  // live rule until the user confirms.
  const apply = async () => {
    if (!shown.from || !shown.to) return;
    const from = fmtYMD(shown.from);
    const to = fmtYMD(shown.to);
    try {
      await patch.mutateAsync({ valid_from: from, valid_upto: to });
      setCommittedFrom(from);
      setCommittedUpto(to);
      setDraft(undefined);
      toast.success(pick(lang, "อัปเดตระยะเวลาแล้ว", "Dates updated"));
      setOpen(false);
    } catch {
      toast.error(pick(lang, "บันทึกไม่สำเร็จ ลองอีกครั้ง", "Couldn’t save, try again"));
    }
  };

  return (
    <RowShell label={label}>
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {!alreadyEnded && (
          <button
            type="button"
            onClick={() => setConfirm("end")}
            disabled={patch.isPending}
            className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[12px] font-medium text-rose-600 transition-colors hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20"
          >
            {pick(lang, "จบตอนนี้", "End now")}
          </button>
        )}
        <button
          type="button"
          onClick={() => setConfirm("extend")}
          disabled={patch.isPending}
          className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[12px] font-medium text-sky-600 transition-colors hover:bg-sky-100 disabled:opacity-50 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300 dark:hover:bg-sky-500/20"
        >
          {pick(lang, "+7 วัน", "+7 days")}
        </button>

        <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirm === "end"
                  ? pick(lang, "จบโปรโมชันตอนนี้?", "End this promotion now?")
                  : pick(lang, "ต่ออายุอีก 7 วัน?", "Extend by 7 days?")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirm === "end"
                  ? pick(
                      lang,
                      "ลูกค้าจะใช้โปรโมชันนี้ไม่ได้ทันที (สิ้นสุดวันนี้) — ย้อนกลับได้ด้วยการตั้งวันสิ้นสุดใหม่",
                      "Customers won’t be able to use it starting today. You can undo this by setting a new end date.",
                    )
                  : pick(
                      lang,
                      `วันสิ้นสุดใหม่จะเป็น ${formatReadableDate(fmtYMD(extendTarget))}`,
                      `The new end date will be ${formatReadableDate(fmtYMD(extendTarget))}.`,
                    )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{pick(lang, "ยกเลิก", "Cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={runConfirmed}
                className={
                  confirm === "end"
                    ? "bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-400"
                    : undefined
                }
              >
                {confirm === "end" ? pick(lang, "จบเลย", "End now") : pick(lang, "ต่ออายุ", "Extend")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Popover
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (!o) setDraft(undefined); // discard an incomplete selection on close
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="group h-auto gap-1.5 px-2 py-1 font-normal text-gray-900 dark:text-gray-100"
            >
              <span className="break-words">{windowSummary({ valid_from: committedFrom, valid_upto: committedUpto }, lang)}</span>
              <CalendarDays className="!h-3.5 !w-3.5 shrink-0 text-gray-400 transition-colors group-hover:text-sky-500" />
            </Button>
          </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0">
          <Calendar
            autoFocus
            mode="range"
            numberOfMonths={2}
            defaultMonth={shown.from}
            selected={shown}
            onSelect={setDraft}
            className="p-3"
          />
          <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2.5">
            <span className="text-[13px] text-gray-500 dark:text-gray-400">
              {windowSummary({ valid_from: fmtYMD(shown.from), valid_upto: fmtYMD(shown.to) }, lang)}
            </span>
            <Button type="button" size="sm" onClick={apply} disabled={!canApply}>
              {pick(lang, "บันทึก", "Apply")}
            </Button>
          </div>
        </PopoverContent>
        </Popover>
      </div>
    </RowShell>
  );
}

/**
 * หน้าร้าน — which storefront(s) this rule applies to (custom_storefront_scope),
 * for companies that run more than one storefront (e.g. CSIDE = cside + subscrub).
 * Reuses the wizard's StorefrontScopePicker (inline mode) so the selected keys and
 * serialization exactly match checkout enforcement. Blank = all storefronts, and
 * single-store tenants see a read-only "all" (the picker auto-hides).
 */
export function StoreScopeRow({
  label,
  formData,
  docName,
  lang,
}: {
  label: string;
  formData: Record<string, any>;
  docName: string;
  lang: Lang;
}) {
  const { data: stores = [] } = useStoreList();
  const [committed, setCommitted] = useState<string>(formData.custom_storefront_scope || "");
  const patch = useRulePatch(docName);

  useEffect(() => setCommitted(formData.custom_storefront_scope || ""), [formData.custom_storefront_scope]);

  const scopeT = (k: string): string =>
    ({
      storefront_scope_all: pick(lang, "ทุกหน้าร้าน", "All storefronts"),
      storefront_scope_ph: pick(lang, "ทุกหน้าร้าน", "All storefronts"),
      storefront_scope: pick(lang, "หน้าร้าน", "Storefronts"),
      storefront_scope_hint: "",
    })[k] ?? k;

  const onChange = async (next: string) => {
    const prev = committed;
    setCommitted(next); // optimistic — host formData doesn't refetch after write
    try {
      await patch.mutateAsync({ custom_storefront_scope: next });
      toast.success(
        next
          ? pick(lang, "อัปเดตหน้าร้านแล้ว", "Storefronts updated")
          : pick(lang, "ใช้ได้ทุกหน้าร้าน", "Applies to all storefronts"),
      );
    } catch {
      setCommitted(prev);
      toast.error(pick(lang, "บันทึกไม่สำเร็จ ลองอีกครั้ง", "Couldn’t save, try again"));
    }
  };

  return (
    <RowShell label={label}>
      {stores.length < 2 ? (
        <span className="text-gray-500 dark:text-gray-400">{pick(lang, "ทุกหน้าร้าน", "All storefronts")}</span>
      ) : (
        <StorefrontScopePicker inline value={committed} onChange={onChange} t={scopeT} />
      )}
    </RowShell>
  );
}

/** ยอดขั้นต่ำ — edit min_amt (the money threshold) via the generic Currency editor. */
export function MinSpendRow({
  label,
  formData,
  docName,
  lang,
}: {
  label: string;
  formData: Record<string, any>;
  docName: string;
  lang: Lang;
}) {
  const [editing, setEditing] = useState(false);
  const [committedAmt, setCommittedAmt] = useState<number>(Number(formData.min_amt) || 0);
  const patch = useRulePatch(docName);

  useEffect(() => setCommittedAmt(Number(formData.min_amt) || 0), [formData.min_amt]);

  const minQty = Number(formData.min_qty) || 0;
  const readText =
    committedAmt > 0
      ? formatMoney(committedAmt, ruleCurrency(formData))
      : minQty > 0
        ? pick(lang, `${minQty} ชิ้น`, `${minQty} items`)
        : pick(lang, "ไม่มีขั้นต่ำ", "No minimum");

  const commit = async (raw: unknown) => {
    const n = Number(raw) || 0;
    if (n < 0) {
      toast.error(pick(lang, "กรอกจำนวนเงินที่ถูกต้อง", "Enter a valid amount"));
      setEditing(false);
      return;
    }
    try {
      await patch.mutateAsync({ min_amt: n });
      setCommittedAmt(n);
      toast.success(pick(lang, "อัปเดตยอดขั้นต่ำแล้ว", "Minimum updated"));
    } catch {
      toast.error(pick(lang, "บันทึกไม่สำเร็จ ลองอีกครั้ง", "Couldn’t save, try again"));
    }
    setEditing(false);
  };

  if (!editing) {
    return (
      <RowShell label={label} onEdit={() => setEditing(true)}>
        <span className="break-words">{readText}</span>
      </RowShell>
    );
  }

  // Edit chromeless — identical to how every other inline Currency field edits
  // (InlineFieldInput on INLINE_INPUT_CLASS, no ring box, no manual ฿ prefix).
  // Currency belongs to the read-mode formatter, not the editor.
  return (
    <RowShell label={label}>
      <InlineFieldInput field={MIN_AMT_FIELD} value={committedAmt} onCommit={commit} onCancel={() => setEditing(false)} placeholder="0" />
    </RowShell>
  );
}
